import React, { memo } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Brightness4,
  Brightness7,
  MenuRounded,
  Support,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { CONFIG } from "../../config";
import { useTranslation } from "react-i18next";

// Custom components
import useNavBar from "./useNavBar";
import SidebarMenu from "./SidebarMenu";
import UserMenu from "./UserMenu";
import OrganizationSelector from "./OrganizationSelector";

/**
 * Main navigation bar container component
 */
const NavBarContainer = ({ handleCollapsed, handleThemeChange }) => {
  const { t } = useTranslation("common");
  const theme = useTheme();

  // Get state and handlers from custom hook
  const {
    user,
    themeMode,
    isMobile,
    selectedOrganisation,
    organisations,
    rotate,
    anchorEl,
    anchorElSidebar,
    userImage,
    handleMenu,
    handleClose,
    handleCloseSidebar,
    handleCollapsedMenu,
    handleOrganisationChange,
    handleLogout,
    setAnchorElSidebar,
    navigate,
  } = useNavBar(handleCollapsed, handleThemeChange);

  return (
    <AppBar position="static">
      <Toolbar>
        {/* Menu/Collapse Button */}
        <MenuRounded
          onClick={(event) => {
            // If isMobile, open menu, else collapse sidebar
            isMobile
              ? setAnchorElSidebar(event.currentTarget)
              : handleCollapsedMenu();
          }}
          style={{ transform: rotate, transition: "all 0.2s linear" }}
          sx={{
            mr: 2,
            ml: "2px",
            color:
              theme.palette.mode === "dark"
                ? theme.palette.primary.main
                : "#fff",
          }}
        />

        {/* Sidebar Menu for Mobile */}
        <SidebarMenu
          anchorEl={anchorElSidebar}
          open={!!anchorElSidebar}
          onClose={handleCloseSidebar}
        />

        {/* Logo/Site Title */}
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          <Link to="/" style={{ textDecoration: "none", color: "white" }}>
            <Typography
              variant="h6"
              sx={
                themeMode === "dark"
                  ? { color: theme.palette.primary.main }
                  : { color: "white" }
              }
            >
              {CONFIG.siteTitle}
            </Typography>
          </Link>
        </Typography>

        {/* Organization Selector (desktop only) */}
        {!isMobile && (
          <OrganizationSelector
            organisations={organisations}
            selectedOrganisation={selectedOrganisation}
            onChange={handleOrganisationChange}
            themeMode={themeMode}
            theme={theme}
          />
        )}

        {/* Theme Toggle Button */}
        <Tooltip
          title={
            themeMode === "dark"
              ? t("common.switch_to_light_mode")
              : t("common.switch_to_dark_mode")
          }
          placement="bottom"
        >
          <IconButton
            aria-label="theme mode"
            sx={
              themeMode === "dark"
                ? { color: theme.palette.primary.main }
                : { color: "white" }
            }
            onClick={handleThemeChange}
          >
            {themeMode === "dark" ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Tooltip>

        {/* Support Button */}
        {CONFIG.support_url && (
          <Tooltip title={t("common.support")} placement="bottom" arrow>
            <IconButton
              aria-label="support"
              sx={
                themeMode === "dark"
                  ? { color: theme.palette.primary.main }
                  : { color: "white" }
              }
              onClick={() => {
                window.open(CONFIG.support_url, "_blank");
              }}
            >
              <Support />
            </IconButton>
          </Tooltip>
        )}

        {/* User Menu */}
        <UserMenu
          anchorEl={anchorEl}
          userImage={userImage}
          user={user}
          themeMode={themeMode}
          onMenuOpen={handleMenu}
          onMenuClose={handleClose}
          onNavigate={navigate}
          onLogout={handleLogout}
          theme={theme}
        />
      </Toolbar>
    </AppBar>
  );
};

NavBarContainer.propTypes = {
  handleCollapsed: PropTypes.func.isRequired,
  handleThemeChange: PropTypes.func.isRequired,
};

export default memo(NavBarContainer);
